import { useEffect, useRef } from 'react';

const COLORS = [
    '#e8e0ef',
    '#e3d7eb',
    '#decde7',
    '#d9c4e2',
    '#d3badf',
    '#ceb0da',
    '#c9a7d6',
    '#c39dd2',
    '#be94cd',
    '#b88ac9',
    '#b380c5',
    '#ad76c0',
    '#a86dbc',
    '#a263b8',
    '#9d5ab3',
    '#9750af',
    '#9246aa',
    '#8c3da6',
    '#8733a1',
    '#75499d',
    '#fff9d1',
    '#fff3b0',
    '#ffec8f',
    '#ffe56d',
    '#ffdd4c',
];

interface Particle {
    x: number;
    y: number;
    z: number;
    vx: number;
    vy: number;
    vz: number;
    angle_x: number;
    angle_y: number;
    angle_z: number;
    width: number;
    height: number;
    color: string;
    lifetime: number;
}

interface UseConfettiProps {
    max_particles?: number;
    speed?: number;
    gravity?: number;
    play: boolean;
}

export const useConfetti = ({ max_particles = 500, speed = 3000, gravity = .3, play }: UseConfettiProps) => {
    const canvas_ref = useRef<HTMLCanvasElement | null>(null);
    const particles_ref = useRef<Particle[]>([]);
    const animation_frame_ref = useRef<number | null>(null);
    const spawn_allowed_ref = useRef(true);

    useEffect(() => {
        const canvas = canvas_ref.current;
        if (!canvas) return;
        const ctx = canvas.getContext('2d');
        if (!ctx) return;

        const resize_canvas = () => {
            canvas.width = window.innerWidth;
            canvas.height = window.innerHeight;
        };
        resize_canvas();
        window.addEventListener('resize', resize_canvas);

        const clear_canvas = () => {
            ctx.clearRect(0, 0, canvas.width, canvas.height);
        };

        if (!play) {
            clear_canvas();
            return;
        }

        const create_particle = (): Particle => ({
            x: Math.random() * canvas.width,
            y: Math.random() * -100,
            z: Math.random() * 200 - 100,
            vx: Math.random() * 12 - 6,
            vy: Math.random() * 12 - 10,
            vz: Math.random() * 4 - 2,
            angle_x: Math.random() * Math.PI * 2,
            angle_y: Math.random() * Math.PI * 2,
            angle_z: Math.random() * Math.PI * 2,
            width: Math.floor(Math.random() * 15 + 5),
            height: Math.floor(Math.random() * 10 + 5),
            color: COLORS[Math.floor(Math.random() * COLORS.length)],
            lifetime: Math.floor(Math.random() * 120 + 60),
        });

        particles_ref.current = Array.from({ length: max_particles }, create_particle);

        setTimeout(() => (spawn_allowed_ref.current = false), speed);

        const update_particles = () => {
            particles_ref.current.forEach((p, index) => {
                p.vy += gravity; // Теперь гравитация регулируется параметром
                p.x += p.vx;
                p.y += p.vy;
                p.z += p.vz;
                p.vx *= 0.98;
                p.vy *= 0.98;
                p.vz *= 0.98;
                p.angle_x += 0.1;
                p.angle_y += 0.1;
                p.angle_z += 0.1;
                p.lifetime--;

                if (p.lifetime <= 0 && spawn_allowed_ref.current) {
                    particles_ref.current[index] = create_particle();
                }
            });
        };

        const draw_particles = () => {
            clear_canvas();
            particles_ref.current.forEach((p) => {
                ctx.save();
                ctx.translate(p.x, p.y);
                ctx.rotate(p.angle_z);
                ctx.fillStyle = p.color;
                ctx.fillRect(-p.width / 2, -p.height / 2, p.width, p.height);
                ctx.restore();
            });
        };

        const animate = () => {
            update_particles();
            draw_particles();
            animation_frame_ref.current = requestAnimationFrame(animate);
        };

        animate();

        return () => {
            if (animation_frame_ref.current) {
                cancelAnimationFrame(animation_frame_ref.current);
            }
            window.removeEventListener('resize', resize_canvas);
            clear_canvas();
        };
    }, [max_particles, speed, gravity, play]); // Добавлен `gravity` в зависимости

    return canvas_ref;
};
